import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, TextInput, TextStyle, View } from 'react-native';
import Svg, { Line, Path } from 'react-native-svg';

import GradientPlate, { GradientStops } from '../common/GradientPlate';
import { Pressable } from '../common/Touchable';
import RedesignModal from '../Modals/RedesignModal';
import { useRedesignScale } from '../Modals/useRedesignScale';
import {
	AGE_RANGES,
	DEFAULT_FRIENDS_FILTER,
	formatRange,
	FriendsFilterValues,
	FriendsGender,
	GENDERS,
	LEVEL_RANGES,
} from './friendsFilter';
import { SOCIAL_FONT } from './socialTheme';

/**
 * The Social screen's filter popup. There is no Figma frame for it; it follows the mockup the
 * product owner supplied (Oct 2026), measured onto the redesign popups' shared 358-wide panel and
 * drawn with their chrome: `RedesignModal` over the dimmed, blurred live screen, the app backdrop
 * inside a gold rim. Numbers are design units, scaled once.
 */
const PANEL = { height: 464, radius: 17, rim: 2 };
const LAYOUT = {
	paddingTop: 54,
	sideRow: 31,
	sideButtons: 22,
	labelHeight: 18,
	labelGap: 7,
	sectionGap: 21,
	rowHeight: 41,
	fieldLabelGap: 6,
	fieldHeight: 37,
	buttonsTop: 18,
	buttonHeight: 44,
	buttonGap: 13,
};
const SQUARE = { width: 39, height: 34, inset: 7, radius: 6 };
const CLOSE = { size: 37, right: -7, top: 1 };

const colors = {
	label: '#FFCF5A',
	value: '#FFF8F4',
	rowFill: '#2E0000',
	rowBorder: '#A11A10',
	rowGlow: '#FF1A00',
	fieldFill: '#2F0800',
	fieldBorder: '#F2A14E',
	placeholder: 'rgba(255,237,224,0.5)',
	panelRim: '#FFC261',
	/** The mockup's panel is the app backdrop at about half brightness (sampled at 12 points). */
	panelTint: 'rgba(8,0,0,0.5)',
	/** The darker scrim the app's other dark-backdrop popups use (Daily Missions' 0.68). */
	scrim: 'rgba(0,0,0,0.68)',
} as const;

const SQUARE_FILL: GradientStops = [
	['0', '#FCB21A'],
	['0.5', '#FA9D12'],
	['1', '#F28E03'],
];
const SQUARE_RIM: GradientStops = [
	['0', '#FFE7A2'],
	['0.5', '#F9B030'],
	['1', '#D87300'],
];
const RESET_FILL: GradientStops = [
	['0', '#FDB20A'],
	['0.4', '#F49D02'],
	['0.85', '#EC8B03'],
	['1', '#EF9700'],
];
const RESET_RIM: GradientStops = [
	['0', '#FFE56E'],
	['0.5', '#FFCB42'],
	['1', '#F2A010'],
];
const FILTER_FILL: GradientStops = [
	['0', '#00CC3A'],
	['0.5', '#00B330'],
	['1', '#009E22'],
];
const FILTER_RIM: GradientStops = [
	['0', '#5CF57E'],
	['0.5', '#1CEE4F'],
	['1', '#2EAC42'],
];
const CLOSE_FILL: GradientStops = [
	['0', '#B22306'],
	['0.5', '#6E0900'],
	['1', '#A00C00'],
];
const CLOSE_RIM: GradientStops = [
	['0', '#FFEA96'],
	['0.5', '#FFB844'],
	['1', '#FF7F23'],
];

// react-native-web drops the browser's focus ring for 'none', which RN's types don't list.
type WebInputStyle = Omit<TextStyle, 'outlineStyle'> & { outlineStyle?: 'none' };
type Glyph = 'prev' | 'next' | 'minus' | 'plus';

/**
 * Icons sit on a GradientPlate, which is absolutely positioned. On web a bare <svg> is not
 * positioned, so the browser paints the plate over it; a View (position: relative in
 * react-native-web, as Text is) keeps the icon on top on every platform.
 */
const IconBox = ({ children }: { children: React.ReactNode }) => (
	<View pointerEvents='none'>{children}</View>
);

const GlyphIcon = ({ glyph, size }: { glyph: Glyph; size: number }) => {
	const stroke = { stroke: '#FFFFFF', strokeWidth: 1.9, strokeLinecap: 'round' as const };
	return (
		<Svg width={size} height={size} viewBox='0 0 16 16'>
			{glyph === 'prev' ? (
				<Path d='M10 2.5L4.5 8L10 13.5' fill='none' strokeLinejoin='round' {...stroke} />
			) : glyph === 'next' ? (
				<Path d='M6 2.5L11.5 8L6 13.5' fill='none' strokeLinejoin='round' {...stroke} />
			) : (
				<>
					<Line x1={1.5} y1={8} x2={14.5} y2={8} {...stroke} />
					{glyph === 'plus' ? <Line x1={8} y1={1.5} x2={8} y2={14.5} {...stroke} /> : null}
				</>
			)}
		</Svg>
	);
};

const ResetIcon = ({ size }: { size: number }) => (
	<Svg width={size} height={size} viewBox='0 0 20 20'>
		<Path
			d='M4.2 7.2A6.6 6.6 0 1 1 3.6 11.6'
			fill='none'
			stroke='#FFFFFF'
			strokeWidth={2.2}
			strokeLinecap='round'
		/>
		<Path
			d='M2.6 3.2V8.2H7.6'
			fill='none'
			stroke='#FFFFFF'
			strokeWidth={2.2}
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
	</Svg>
);

const FunnelIcon = ({ size }: { size: number }) => (
	<Svg width={size} height={size} viewBox='0 0 20 20'>
		<Path
			d='M1.5 2.5H18.5L11.8 10.6V16.6L8.2 18.4V10.6L1.5 2.5Z'
			fill='#FFFFFF'
			strokeLinejoin='round'
		/>
	</Svg>
);

type Props = {
	value: FriendsFilterValues;
	onApply: (value: FriendsFilterValues) => void;
	onClose: () => void;
};

const FriendsFilterModal = ({ value, onApply, onClose }: Props) => {
	const { t } = useTranslation();
	const scale = useRedesignScale({ fitHeight: PANEL.height });
	const s = (n: number) => n * scale;
	const styles = useMemo(() => createStyles(scale), [scale]);
	const [draft, setDraft] = useState<FriendsFilterValues>(value);
	const applyRef = useRef<View>(null);

	const anyLabel = t('T_SITE_FILTER_ANY', { defaultValue: 'Any' });
	const genderLabel = (gender: FriendsGender) =>
		gender === 'male'
			? t('T_SITE_PROFILE_MODAL_GENDER_MALE', { defaultValue: 'Male' })
			: gender === 'female'
				? t('T_SITE_PROFILE_MODAL_GENDER_FEMALE', { defaultValue: 'Female' })
				: anyLabel;

	const cycleGender = (step: number) =>
		setDraft(d => {
			const next = (GENDERS.indexOf(d.gender) + step + GENDERS.length) % GENDERS.length;
			return { ...d, gender: GENDERS[next] };
		});
	const stepRange = (key: 'age' | 'level', ranges: unknown[], step: number) =>
		setDraft(d => ({ ...d, [key]: Math.min(ranges.length - 1, Math.max(0, d[key] + step)) }));

	const square = (glyph: Glyph, label: string, onPress: () => void, disabled = false) => (
		<Pressable
			accessibilityRole='button'
			accessibilityLabel={label}
			disabled={disabled}
			onPress={onPress}
			style={[styles.square, disabled && styles.disabled]}
		>
			<GradientPlate
				width={s(SQUARE.width)}
				height={s(SQUARE.height)}
				radius={s(SQUARE.radius)}
				stops={SQUARE_FILL}
				strokeStops={SQUARE_RIM}
				strokeWidth={Math.max(1, s(1))}
			/>
			<IconBox>
				<GlyphIcon glyph={glyph} size={s(16)} />
			</IconBox>
		</Pressable>
	);

	const row = (
		label: string,
		text: string,
		left: React.ReactNode,
		right: React.ReactNode,
		first = false,
	) => (
		<>
			<Text style={[styles.label, !first && styles.labelSpaced]}>{label}</Text>
			<View style={styles.row}>
				{left}
				<Text numberOfLines={1} style={styles.value}>
					{text}
				</Text>
				{right}
			</View>
		</>
	);

	const closeButton = (
		<Pressable
			accessibilityRole='button'
			accessibilityLabel={t('T_SITE_A11Y_CLOSE_FILTERS', { defaultValue: 'Close filters' })}
			onPress={onClose}
			hitSlop={6}
			style={styles.close}
		>
			<GradientPlate
				width={s(CLOSE.size)}
				height={s(CLOSE.size)}
				radius={s(CLOSE.size / 2)}
				stops={CLOSE_FILL}
				strokeStops={CLOSE_RIM}
				strokeWidth={Math.max(1, s(2))}
			/>
			<IconBox>
				<Svg width={s(15)} height={s(15)} viewBox='0 0 15 15'>
					<Line
						x1={2}
						y1={2}
						x2={13}
						y2={13}
						stroke='#FFEAA4'
						strokeWidth={2.8}
						strokeLinecap='round'
					/>
					<Line
						x1={13}
						y1={2}
						x2={2}
						y2={13}
						stroke='#FFEAA4'
						strokeWidth={2.8}
						strokeLinecap='round'
					/>
				</Svg>
			</IconBox>
		</Pressable>
	);

	return (
		<RedesignModal
			scale={scale}
			onClose={onClose}
			panel={{
				height: PANEL.height,
				radius: PANEL.radius,
				borderColor: colors.panelRim,
				borderWidth: PANEL.rim,
				tint: colors.panelTint,
				scrimColor: colors.scrim,
				overScreen: true,
			}}
			panelOverlay={closeButton}
			keyboardSubmitRef={applyRef}
		>
			<View style={styles.body}>
				{row(
					t('T_SITE_PROFILE_MODAL_GENDER', { defaultValue: 'Gender' }).toUpperCase(),
					genderLabel(draft.gender),
					square('prev', 'Previous gender', () => cycleGender(-1)),
					square('next', 'Next gender', () => cycleGender(1)),
					true,
				)}
				{row(
					t('T_SITE_PROFILE_MODAL_AGE', { defaultValue: 'Age' }).toUpperCase(),
					formatRange(AGE_RANGES[draft.age], anyLabel),
					square(
						'minus',
						'Lower age range',
						() => stepRange('age', AGE_RANGES, -1),
						draft.age === 0,
					),
					square(
						'plus',
						'Higher age range',
						() => stepRange('age', AGE_RANGES, 1),
						draft.age === AGE_RANGES.length - 1,
					),
				)}
				{row(
					t('T_SITE_PROFILE_LEVEL', { defaultValue: 'Level' }).toUpperCase(),
					formatRange(LEVEL_RANGES[draft.level], anyLabel),
					square(
						'minus',
						'Lower level range',
						() => stepRange('level', LEVEL_RANGES, -1),
						draft.level === 0,
					),
					square(
						'plus',
						'Higher level range',
						() => stepRange('level', LEVEL_RANGES, 1),
						draft.level === LEVEL_RANGES.length - 1,
					),
				)}

				<Text style={[styles.label, styles.labelSpaced]}>
					{t('T_SITE_PROFILE_MODAL_LOCATION', { defaultValue: 'Location' }).toUpperCase()}
				</Text>
				<View style={styles.field}>
					<TextInput
						value={draft.location}
						onChangeText={location => setDraft(d => ({ ...d, location }))}
						placeholder={t('T_SITE_PROFILE_MODAL_LOCATION', { defaultValue: 'Location' })}
						placeholderTextColor={colors.placeholder}
						autoCorrect={false}
						returnKeyType='done'
						onSubmitEditing={() => onApply(draft)}
						style={styles.input}
					/>
				</View>

				<View style={styles.buttons}>
					<Pressable
						accessibilityRole='button'
						accessibilityLabel={t('T_SITE_A11Y_RESET_FILTERS', { defaultValue: 'Reset filters' })}
						onPress={() => setDraft(DEFAULT_FRIENDS_FILTER)}
						style={[styles.button, styles.resetGlow]}
					>
						<GradientPlate
							width={s(151)}
							height={s(LAYOUT.buttonHeight)}
							radius={s(10)}
							stops={RESET_FILL}
							strokeStops={RESET_RIM}
							strokeWidth={Math.max(1, s(1.5))}
						/>
						<IconBox>
							<ResetIcon size={s(20)} />
						</IconBox>
						<Text style={styles.buttonLabel}>
							{t('T_SITE_FILTER_RESET', { defaultValue: 'Reset' }).toUpperCase()}
						</Text>
					</Pressable>
					<Pressable
						ref={applyRef}
						accessibilityRole='button'
						accessibilityLabel={t('T_SITE_A11Y_APPLY_FILTERS', { defaultValue: 'Apply filters' })}
						onPress={() => onApply(draft)}
						style={[styles.button, styles.filterGlow]}
					>
						<GradientPlate
							width={s(151)}
							height={s(LAYOUT.buttonHeight)}
							radius={s(10)}
							stops={FILTER_FILL}
							strokeStops={FILTER_RIM}
							strokeWidth={Math.max(1, s(1.5))}
						/>
						<IconBox>
							<FunnelIcon size={s(20)} />
						</IconBox>
						<Text style={styles.buttonLabel}>
							{t('T_SITE_FILTER_APPLY', { defaultValue: 'Filter' }).toUpperCase()}
						</Text>
					</Pressable>
				</View>
			</View>
		</RedesignModal>
	);
};

const createStyles = (scale: number) => {
	const s = (n: number) => n * scale;
	return StyleSheet.create({
		body: {
			flex: 1,
			paddingTop: s(LAYOUT.paddingTop),
			alignItems: 'center',
		},
		label: {
			height: s(LAYOUT.labelHeight),
			marginBottom: s(LAYOUT.labelGap),
			fontFamily: SOCIAL_FONT,
			fontSize: s(15),
			lineHeight: s(LAYOUT.labelHeight),
			fontWeight: '900',
			color: colors.label,
			textAlign: 'center',
		},
		labelSpaced: {
			marginTop: s(LAYOUT.sectionGap),
		},
		row: {
			width: s(358 - LAYOUT.sideRow * 2),
			height: s(LAYOUT.rowHeight),
			borderRadius: s(10),
			borderWidth: Math.max(1, s(1)),
			borderColor: colors.rowBorder,
			backgroundColor: colors.rowFill,
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'space-between',
			paddingHorizontal: s(SQUARE.inset) - Math.max(1, s(1)),
			shadowColor: colors.rowGlow,
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 0.35,
			shadowRadius: s(6),
		},
		square: {
			width: s(SQUARE.width),
			height: s(SQUARE.height),
			alignItems: 'center',
			justifyContent: 'center',
		},
		disabled: {
			opacity: 0.45,
		},
		value: {
			flex: 1,
			fontFamily: SOCIAL_FONT,
			fontSize: s(15),
			fontWeight: '800',
			color: colors.value,
			textAlign: 'center',
		},
		field: {
			width: s(358 - LAYOUT.sideRow * 2),
			height: s(LAYOUT.fieldHeight),
			marginTop: s(LAYOUT.fieldLabelGap - LAYOUT.labelGap),
			borderRadius: s(10),
			borderWidth: Math.max(1, s(1)),
			borderColor: colors.fieldBorder,
			backgroundColor: colors.fieldFill,
			justifyContent: 'center',
		},
		input: {
			height: '100%',
			paddingHorizontal: s(13),
			paddingVertical: 0,
			fontFamily: SOCIAL_FONT,
			fontSize: s(14.5),
			fontWeight: '500',
			color: colors.value,
			outlineStyle: 'none',
		} as WebInputStyle as TextStyle,
		buttons: {
			width: s(358 - LAYOUT.sideButtons * 2),
			marginTop: s(LAYOUT.buttonsTop),
			flexDirection: 'row',
			justifyContent: 'space-between',
		},
		button: {
			width: s(151),
			height: s(LAYOUT.buttonHeight),
			borderRadius: s(10),
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'center',
			gap: s(9),
		},
		resetGlow: {
			shadowColor: '#FF9A00',
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 0.55,
			shadowRadius: s(8),
		},
		filterGlow: {
			shadowColor: '#2BFF5A',
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 0.45,
			shadowRadius: s(8),
		},
		buttonLabel: {
			fontFamily: SOCIAL_FONT,
			fontSize: s(16),
			fontWeight: '900',
			color: '#FFFFFF',
			textShadowColor: 'rgba(80,30,0,0.45)',
			textShadowOffset: { width: 0, height: s(1) },
			textShadowRadius: s(1.5),
		},
		close: {
			position: 'absolute',
			right: s(CLOSE.right),
			top: s(CLOSE.top),
			width: s(CLOSE.size),
			height: s(CLOSE.size),
			borderRadius: s(CLOSE.size / 2),
			alignItems: 'center',
			justifyContent: 'center',
			shadowColor: '#FF6A00',
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 0.6,
			shadowRadius: s(6),
		},
	});
};

export default FriendsFilterModal;
